import { useCallback, useRef, useState } from 'react';
import { isValidContainerNumber } from '../utils/containerValidator';

// Movetypes for which the ISO 6346 check should fire. Mirrors the legacy
// chkDigit gate in cms.js — for any other movetype, validation is a no-op
// because the container field may legitimately be empty or non-standard.
const VALIDATED_MOVETYPES = new Set(['IMPORT', 'EXPORT', 'RAIL TO PORT']);

export interface UseContainerValidationReturn {
  // True when the current value fails validation. Drives field highlight +
  // inline "Container format invalid" message. Stays true across blurs until
  // the user edits the value or it becomes valid.
  warned: boolean;
  // True while the Keep / Edit confirm modal should be visible.
  modalOpen: boolean;
  // The value that triggered the warning — used in the modal copy.
  warnedValue: string;
  // Run when the order data first loads (or when navigating between orders).
  // Highlights an invalid value silently — never opens the modal.
  validateInitial: (value: string, moveType: string | null | undefined) => void;
  // Run from the input's onBlur. Opens the modal only if the user has edited
  // the value since load and the new value hasn't already been accepted.
  validate: (value: string, moveType: string | null | undefined) => void;
  // Call from the input's onChange. Clears the highlight as the user types
  // and marks the field as edited so the next blur is eligible for the modal.
  clearWarning: () => void;
  // Close the modal. Treats the current warned value as accepted so tabbing
  // back through the same value doesn't re-prompt.
  closeModal: () => void;
}

const shouldEvaluate = (value: string, moveType: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  if (!moveType || !VALIDATED_MOVETYPES.has(moveType)) return null;
  return trimmed;
};

export function useContainerValidation(): UseContainerValidationReturn {
  const [warned, setWarned] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [warnedValue, setWarnedValue] = useState('');
  // Last invalid value the user has implicitly (initial load) or explicitly
  // (Keep / Edit) accepted. Cleared on edit. Re-prompting for the same value
  // would just nag the dispatcher about something they've already seen.
  const acceptedRef = useRef<string | null>(null);
  // Has the user touched the field this session? Initial-load validation
  // highlights but never opens the modal until the user actually edits.
  const dirtyRef = useRef(false);

  const validateInitial = useCallback(
    (value: string, moveType: string | null | undefined) => {
      const trimmed = shouldEvaluate(value, moveType);
      dirtyRef.current = false;
      if (!trimmed || isValidContainerNumber(trimmed, { strict: false })) {
        setWarned(false);
        acceptedRef.current = null;
        return;
      }
      setWarned(true);
      setWarnedValue(trimmed);
      acceptedRef.current = trimmed;
    },
    [],
  );

  const validate = useCallback(
    (value: string, moveType: string | null | undefined) => {
      const trimmed = shouldEvaluate(value, moveType);
      if (!trimmed) {
        setWarned(false);
        return;
      }
      if (isValidContainerNumber(trimmed, { strict: false })) {
        setWarned(false);
        acceptedRef.current = null;
        return;
      }
      setWarned(true);
      setWarnedValue(trimmed);
      // Only nag with the modal if the user has actually changed something
      // and we haven't already prompted for this exact value.
      if (dirtyRef.current && trimmed !== acceptedRef.current) {
        setModalOpen(true);
      }
    },
    [],
  );

  const clearWarning = useCallback(() => {
    dirtyRef.current = true;
    acceptedRef.current = null;
    setWarned(false);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    acceptedRef.current = warnedValue;
  }, [warnedValue]);

  return {
    warned,
    modalOpen,
    warnedValue,
    validateInitial,
    validate,
    clearWarning,
    closeModal,
  };
}